// A small "Studio Circle" strip on the home page — informs clients about
// the 5-visit loyalty perk without needing a form or account. Purely
// informational; the actual punch tracking lives in the admin panel.
import { Award, CalendarClock, Sparkles } from "lucide-react";

const STEPS = [
  {
    icon: CalendarClock,
    title: "Book any session",
    body: "Every therapeutic massage or custom facial you book counts as one visit — nothing to sign up for.",
  },
  {
    icon: Sparkles,
    title: "Bella keeps count",
    body: "Your visits are tracked quietly in the studio ledger, tied to the email you book with. No app, no card in your wallet.",
  },
  {
    icon: Award,
    title: "Five visits · free facial",
    body: "On your fifth completed visit, Bella hands you a free 30-minute facial to book at your next opening.",
  },
];

export default function StudioCircle() {
  return (
    <section
      id="studio-circle"
      data-testid="studio-circle-section"
      className="relative bg-[#FAF6E8] py-20 md:py-24 overflow-hidden"
    >
      <div
        aria-hidden
        className="pointer-events-none absolute -top-32 -left-24 w-96 h-96 rounded-full bg-[#EAD584]/20 blur-3xl"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -bottom-32 -right-24 w-96 h-96 rounded-full bg-[#0F1F4E]/8 blur-3xl"
      />

      <div className="relative max-w-6xl mx-auto px-6 md:px-10">
        <div className="grid grid-cols-12 gap-8 md:gap-12 items-start">
          <div className="col-span-12 md:col-span-4">
            <div className="overline-gold mb-4 inline-flex items-center gap-2">
              <Award className="w-3.5 h-3.5" aria-hidden="true" />
              Studio Circle
            </div>
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl font-light leading-tight tracking-tight bb-ink">
              Five visits earn you a <span className="italic text-[#C9A65A]">free facial</span>.
            </h2>
            <p className="mt-5 bb-ink-soft text-sm md:text-base font-light leading-relaxed max-w-md">
              A quiet loyalty program — no punch card in your wallet, no
              app, no points to redeem. Just Bella keeping track and
              handing you a free 30-minute facial the day you hit five
              completed sessions.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <a
                href="#services"
                data-testid="studio-circle-see-services"
                className="btn-shine inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-xs uppercase tracking-wider bg-[#0F1F4E] text-[#EAD584] hover:bg-[#1B3370] transition-colors"
              >
                See services
              </a>
              <a
                href="#booking"
                data-testid="studio-circle-book-first"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-xs uppercase tracking-wider bb-ink border border-[#0F1F4E]/30 hover:bg-[#0F1F4E] hover:text-[#FAF6E8] transition-colors"
              >
                Book your first visit
              </a>
            </div>
          </div>

          <ol className="col-span-12 md:col-span-8 grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-5 list-none counter-reset:step">
            {STEPS.map((s, i) => {
              const Icon = s.icon;
              return (
                <li
                  key={s.title}
                  data-testid={`studio-circle-step-${i + 1}`}
                  className="relative bg-white border border-[#D9CFA0] rounded-lg p-5 md:p-6 flex flex-col h-full"
                >
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-9 h-9 rounded-full bg-[#0F1F4E] text-[#EAD584] grid place-items-center">
                      <Icon className="w-4 h-4" aria-hidden="true" />
                    </div>
                    <span className="font-serif text-2xl text-[#0F1F4E]/25 leading-none tabular-nums">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                  </div>
                  <h3 className="font-serif text-lg md:text-xl bb-ink font-light leading-snug">
                    {s.title}
                  </h3>
                  <p className="mt-2 bb-ink-soft text-xs md:text-sm font-light leading-relaxed">
                    {s.body}
                  </p>
                </li>
              );
            })}
          </ol>
        </div>

        <p className="mt-10 text-[11px] uppercase tracking-[0.25em] text-[#0F1F4E]/55 text-center">
          Studio Circle · A quiet perk from Beauty &amp; Balance by Bella · Sacramento, CA
        </p>
      </div>
    </section>
  );
}
